import { Box, Divider, Flex } from "@chakra-ui/layout";
import { Button, HStack, Image, Text } from "@chakra-ui/react";
import { useEthers } from "@usedapp/core";
import axios from "axios";
import BigNumber from "bignumber.js";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoIosHeart, IoIosHeartEmpty } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import Web3 from "web3";
import { colors } from "../core/constants/colors";
import { I18N_NAMESPACE } from "../core/constants/i18n";
import { INftMetadata } from "../core/models/nft";
import { uriToHttps } from "../core/utils/ipfs";
import { formatNumber } from "../core/utils/number";
import { generateNonce } from "../core/utils/securityUtils";
import { nftService } from "../services/nft.service";
import { userService } from "../services/user.service";
import { useAppSelector } from "../store/hook";
import { NftResource } from "./NftResource";

interface Props {
  // nft: IMoralisNft;
  // listedNft: IListedNft | undefined;
  isListed?: boolean;
  bTokenAddress?: string;
  price?: number;
  collectionName?: string;
  nftName?: string;
  image?: string;
  tokenUri?: string;
  ownerOf: string | undefined;
  tokenAddress: string;
  tokenId: string;
  isLoading?: boolean;
  favorites?: number;
  isFavorite?: boolean;
}

export const NftItem = React.memo((props: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation([I18N_NAMESPACE.PROFILE]);

  const { account } = useEthers();
  const supportedTokens = useAppSelector((state) => state.token.supportedTokens);
  const token = useMemo(() => {
    const id = supportedTokens.allIds.find((id) => supportedTokens.byId[id].address === props.bTokenAddress);
    return !!id ? supportedTokens.byId[id] : null;
  }, [supportedTokens, props.bTokenAddress]);

  const isOwned = useMemo(() => props.ownerOf?.toLowerCase() === account?.toLowerCase(), [props.ownerOf, account]);
  const [isHoverFavorite, setIsHoverFavorite] = useState<boolean>(false);
  const [favorites, setFavorites] = useState<number>(props.favorites ?? 0);
  const [isFavorite, setIsFavorite] = useState<boolean>(props.isFavorite || false);

  // const [nft, setNft] = useState(props.nft);
  const [metadata, setMetata] = useState<INftMetadata>({
    image: props.image,
    name: props.nftName,
  });

  useEffect(() => {
    async function getMetadata() {
      if (props.tokenUri) {
        const uri = uriToHttps(props.tokenUri);
        if (!uri) return;

        const result = await axios.get(uri);
        setMetata(result.data);
      }
    }

    getMetadata();
  }, [props.tokenUri]);

  const onPressItem = () => {
    navigate(`/assets/${props.tokenAddress}/${props.tokenId}`);
  };

  const getNumberFavorite = async () => {
    const result = await nftService.getNumberFavorite(props.tokenAddress, props.tokenId);
    if (result?.data !== null && result?.data !== undefined) {
      setFavorites(result.data);
    }
  };

  const addFavorite = async () => {
    if (account) {
      const response = await userService.getProfileById(account);

      if (response.data) {
        const result = await nftService.addFavorite(props.tokenAddress, props.tokenId, account);
        if (result.data) {
          setIsFavorite(true);
          getNumberFavorite();
        }
      } else {
        try {
          const web3 = new Web3((window as any).web3.currentProvider);
          await web3.eth.sign(web3.utils.fromUtf8(`${generateNonce(16, "hex")}`), account);
          const result = await nftService.addFavorite(props.tokenAddress, props.tokenId, account);
          if (result.data) {
            setIsFavorite(true);
            getNumberFavorite();
          }
        } catch {}
      }
    }
  };

  const removeFavorite = async () => {
    if (account) {
      const response = await userService.getProfileById(account);

      if (response.data) {
        const result = await nftService.removeFavorite(props.tokenAddress, props.tokenId, account);
        if (result.data) {
          setIsFavorite(false);
          getNumberFavorite();
        }
      } else {
        try {
          const web3 = new Web3((window as any).web3.currentProvider);
          await web3.eth.sign(web3.utils.fromUtf8(`${generateNonce(16, "hex")}`), account);
          const result = await nftService.removeFavorite(props.tokenAddress, props.tokenId, account);
          if (result.data) {
            setIsFavorite(false);
            getNumberFavorite();
          }
        } catch {}
      }
    }
  };

  const FavoriteDetail = () => {
    return (
      <>
        {isFavorite ? (
          <HStack spacing={1}>
            <IoIosHeart
              onClick={(e) => {
                e.stopPropagation();
                removeFavorite();
              }}
              cursor={"pointer"}
            />
            <Text fontSize={"sm"} fontWeight={500}>
              {favorites}
            </Text>
          </HStack>
        ) : (
          <HStack
            spacing={1}
            onMouseEnter={() => setIsHoverFavorite(true)}
            onMouseLeave={() => setIsHoverFavorite(false)}
          >
            {isHoverFavorite ? (
              <IoIosHeart
                onClick={(e) => {
                  e.stopPropagation();
                  addFavorite();
                }}
                cursor={"pointer"}
              />
            ) : (
              <IoIosHeartEmpty cursor={"pointer"} />
            )}
            <Text fontSize={"sm"} fontWeight={500}>
              {favorites}
            </Text>
          </HStack>
        )}
      </>
    );
  };

  return (
    <Flex
      borderWidth="1px"
      borderRadius="lg"
      flexDirection="column"
      overflow="hidden"
      onClick={onPressItem}
      transition={"all 0.1s ease 0s"}
      _hover={{
        boxShadow: "lg",
        transform: "translateY(-4px)",
      }}
    >
      <NftResource image={metadata.image} animationUrl={metadata.animation_url} muted />

      <Flex padding={3} flex={1} flexDirection="column">
        <Flex justifyContent="space-between" flex={1} gridGap={"20px"}>
          <Box flex={1} width={"0%"}>
            <Box color="gray.500" fontWeight="semibold" letterSpacing="wide" fontSize="xs" textTransform="uppercase">
              {props.collectionName}
            </Box>
            <Box mt="1" fontWeight="semibold" as="h4" lineHeight="tight" isTruncated>
              {metadata.name}
            </Box>
          </Box>
        </Flex>

        {!!props.isListed && (
          <Box marginTop={2}>
            {/* <Text fontSize="13px" fontWeight="500" color={colors.primary2} textAlign="right">
              Price
            </Text> */}
            <Flex alignItems="center">
              <Image src={token?.logoUrl} height="16px" width="16px" marginRight="4px" />
              <Text fontSize="14px" color={colors.primary1} fontWeight="700">
                {props?.price
                  ? formatNumber(new BigNumber(props.price).div(Math.pow(10, token?.decimals || 0)).toNumber())
                  : ""}
              </Text>
            </Flex>
          </Box>
        )}

        {(isOwned || (!isOwned && props.isListed)) && (
          <Box m="8px 0px">
            <Divider />
          </Box>
        )}

        <Box>
          {isOwned && (
            <Button colorScheme={props.isListed ? "red" : "blue"} padding="0px 30px" size="sm">
              {props.isListed ? t("Delist") : t("List")}
            </Button>
          )}

          {!isOwned && props.isListed && (
            <Button colorScheme={"blue"} size="sm">
              {t("Buy now")}
            </Button>
          )}
        </Box>

        <Box m="8px 0px">
          <Divider />
        </Box>

        <Flex alignItems="end" justifyContent="end" fontSize={"14px"} fontWeight={500} padding={"4px"}>
          <FavoriteDetail />
        </Flex>
      </Flex>
    </Flex>
  );
});
